// Seller products, tiers, versions and files — docs/plans/marketplace-commerce.md §3.1, §3.4, §6.2.8 and M-D5 (M4).
// Shared by the server (authoritative checks) and the dashboard (the same limits shown before a request is sent).
import type { CategorySlug, License } from "../../api/types";
import { CATEGORY_SLUGS } from "../../lib/categories";
import { hasControlCharacter } from "../identity/chars";

export const PRODUCT_TITLE_MAX_LENGTH = 120;
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 5000;
export const PRODUCT_TAGS_MAX = 10;
export const PRODUCT_TAG_MAX_LENGTH = 30;
export const VERSION_CHANGELOG_MAX_LENGTH = 2000;
/** Real products a Person may create per 24 h (spec silent; conservative, like the store limit). */
export const PRODUCTS_PER_DAY_LIMIT = 20;

/** Cover presets until sellers can upload product images. Keys are public/img families. */
export const PRODUCT_IMAGE_KEYS = [
  "p-minimal-ui-kit",
  "p-nature-kit",
  "p-modern-lobby-kit",
  "p-tycoon-kit",
  "p-vehicle-pack",
  "p-interaction-system",
  "p-marketplace-coast-kit",
] as const;
export type ProductImageKey = (typeof PRODUCT_IMAGE_KEYS)[number];

export const LICENSE_KINDS = ["personal", "commercial", "extended"] as const satisfies readonly License[];

/** Tier prices are whole cents in EUR; the ceiling keeps them inside a Postgres integer with room to spare. */
export const TIER_PRICE_MAX_CENTS = 1_000_000;

// ── Upload rules (§6.2.8) ───────────────────────────────────────────────────────────────────────

export const MAX_FILE_BYTES = 500 * 1024 * 1024;
export const MAX_VERSION_BYTES = 2 * 1024 * 1024 * 1024;
/** Fixed part size chosen by the server: every part but the last is exactly this long. */
export const UPLOAD_PART_BYTES = 16 * 1024 * 1024;
/** Presigned part URLs are valid for one hour. */
export const UPLOAD_URL_TTL_SECONDS = 3600;
/** Files per version (spec silent; bounds the upload plan and the version page). */
export const MAX_FILES_PER_VERSION = 100;
/**
 * A completion still `completing` after this long is presumed dead: the server lets a retry take it over and the
 * dashboard re-sends completion (storage calls time out after 30 s, so a live completion never gets close).
 */
export const COMPLETION_STALE_MINUTES = 10;
/** Storage per store across every version's files that aren't rejected (spec silent; caps what one store can hold). */
export const STORE_STORAGE_LIMIT_BYTES = 10 * 1024 * 1024 * 1024;
/** Bytes a Person may start uploading per 24 h, deleted and rejected files included (spec silent; caps abuse). */
export const DAILY_UPLOAD_LIMIT_BYTES = 5 * 1024 * 1024 * 1024;

/** The extension allowlist and the content types accepted for each (the server stores the first one). */
export const UPLOAD_TYPES = {
  rbxm: ["application/octet-stream"],
  rbxmx: ["application/xml", "text/xml", "application/octet-stream"],
  rbxl: ["application/octet-stream"],
  fbx: ["application/octet-stream"],
  obj: ["text/plain", "model/obj", "application/octet-stream"],
  gltf: ["model/gltf+json", "application/json", "application/octet-stream"],
  glb: ["model/gltf-binary", "application/octet-stream"],
  png: ["image/png"],
  jpg: ["image/jpeg"],
  wav: ["audio/wav", "audio/x-wav", "audio/wave", "audio/vnd.wave"],
  mp3: ["audio/mpeg", "audio/mp3"],
  ogg: ["audio/ogg", "application/ogg"],
  zip: ["application/zip", "application/x-zip-compressed"],
  pdf: ["application/pdf"],
  lua: ["text/x-lua", "text/plain", "application/octet-stream"],
  luau: ["text/x-lua", "text/plain", "application/octet-stream"],
  txt: ["text/plain"],
} as const satisfies Record<string, readonly string[]>;
export type UploadExtension = keyof typeof UPLOAD_TYPES;
export const UPLOAD_EXTENSIONS = Object.keys(UPLOAD_TYPES) as UploadExtension[];

/** `completing`: the server is finishing the multipart upload with storage; not ready to publish. */
export type UploadStatus = "pending" | "completing" | "uploaded" | "clean" | "rejected";

export type ProductField = "title" | "description" | "category" | "tags" | "imageKey";
export type Check<T, F extends string = string> = { ok: true; value: T } | { ok: false; field: F; message: string };

function singleLine(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function text(
  field: ProductField,
  value: unknown,
  { max, label, multiline = false, required = false }: { max: number; label: string; multiline?: boolean; required?: boolean },
): Check<string, ProductField> {
  if (typeof value !== "string") return { ok: false, field, message: `${label} must be text.` };
  const cleaned = multiline ? value.replace(/\r\n?/g, "\n").trim() : singleLine(value);
  if (hasControlCharacter(multiline ? cleaned.replace(/\n/g, "") : cleaned)) {
    return { ok: false, field, message: `${label} contains characters that aren't allowed.` };
  }
  if (required && cleaned.length === 0) return { ok: false, field, message: `Add a ${label.toLowerCase()}.` };
  if ([...cleaned].length > max) return { ok: false, field, message: `${label} can be up to ${max} characters.` };
  return { ok: true, value: cleaned };
}

export interface ProductDetails {
  title: string;
  description: string;
  category: CategorySlug;
  tags: string[];
  imageKey: ProductImageKey;
}

const PRODUCT_CHECKS: { [K in ProductField]: (value: unknown) => Check<ProductDetails[K], ProductField> } = {
  title: (value) => text("title", value, { max: PRODUCT_TITLE_MAX_LENGTH, label: "Title", required: true }),
  description: (value) =>
    text("description", value, { max: PRODUCT_DESCRIPTION_MAX_LENGTH, label: "Description", multiline: true }),
  category: (value) =>
    typeof value === "string" && (CATEGORY_SLUGS as readonly string[]).includes(value)
      ? { ok: true, value: value as CategorySlug }
      : { ok: false, field: "category", message: "Choose a category." },
  tags: (value) => {
    if (!Array.isArray(value)) return { ok: false, field: "tags", message: "Tags must be a list." };
    const tags: string[] = [];
    for (const item of value) {
      const tag = text("tags", item, { max: PRODUCT_TAG_MAX_LENGTH, label: "A tag" });
      if (!tag.ok) return tag;
      const normalized = tag.value.toLowerCase();
      if (normalized && !tags.includes(normalized)) tags.push(normalized);
    }
    if (tags.length > PRODUCT_TAGS_MAX) return { ok: false, field: "tags", message: `Use up to ${PRODUCT_TAGS_MAX} tags.` };
    return { ok: true, value: tags };
  },
  imageKey: (value) =>
    typeof value === "string" && (PRODUCT_IMAGE_KEYS as readonly string[]).includes(value)
      ? { ok: true, value: value as ProductImageKey }
      : { ok: false, field: "imageKey", message: "Choose a cover image." },
};

/** Validates the product fields present in `input`; with `requireAll`, title and category must be there too. */
export function checkProductDetails(
  input: Record<string, unknown>,
  { requireAll }: { requireAll: boolean },
): Check<Partial<ProductDetails>, ProductField> {
  const value: Partial<ProductDetails> = {};
  for (const field of Object.keys(PRODUCT_CHECKS) as ProductField[]) {
    if (input[field] === undefined) {
      if (requireAll && (field === "title" || field === "category")) {
        return PRODUCT_CHECKS[field](undefined) as Check<Partial<ProductDetails>, ProductField>;
      }
      continue;
    }
    const result = PRODUCT_CHECKS[field](input[field]);
    if (!result.ok) return result;
    (value as Record<ProductField, unknown>)[field] = result.value;
  }
  return { ok: true, value };
}

export type ProductPublishRequirement = "description" | "tier" | "free_tiers" | "version" | "files" | "files_ready";

// ── M-D5 ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Before Stripe, a real (non-demo) store may only have free tiers on a published product. Priced tiers can exist on
 * drafts; every tier write and every publish asks this with the product's status after the write.
 */
export function tierPriceAllowed({
  isDemoStore,
  productPublished,
  priceCents,
}: {
  isDemoStore: boolean;
  productPublished: boolean;
  priceCents: number;
}): boolean {
  return priceCents === 0 || isDemoStore || !productPublished;
}

export function checkTierPrice(value: unknown): Check<number, "priceCents"> {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > TIER_PRICE_MAX_CENTS) {
    return { ok: false, field: "priceCents", message: "Price must be a whole number of cents from 0." };
  }
  return { ok: true, value };
}

// ── Versions ────────────────────────────────────────────────────────────────────────────────────

const VERSION_PATTERN = /^[0-9A-Za-z][0-9A-Za-z.+-]{0,39}$/;

export function checkVersionInput(input: Record<string, unknown>): Check<{ version: string; changelog: string }, "version" | "changelog"> {
  const version = typeof input.version === "string" ? input.version.trim() : "";
  if (!VERSION_PATTERN.test(version)) {
    return { ok: false, field: "version", message: "Use up to 40 letters, numbers, dots, + or -, like 1.0.0." };
  }
  const changelog = input.changelog === undefined ? { ok: true as const, value: "" } : text("description", input.changelog, {
    max: VERSION_CHANGELOG_MAX_LENGTH,
    label: "Changelog",
    multiline: true,
  });
  if (!changelog.ok) return { ok: false, field: "changelog", message: changelog.message };
  return { ok: true, value: { version, changelog: changelog.value } };
}

// ── Files ───────────────────────────────────────────────────────────────────────────────────────

export type UploadField = "filename" | "sizeBytes" | "contentType";

export interface UploadRequest {
  filename: string;
  extension: UploadExtension;
  sizeBytes: number;
  contentType: string;
}

/** The lowercase extension of a filename, or "" when it has none. */
export function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(dot + 1).toLowerCase() : "";
}

/**
 * Validates an upload request against the allowlist and limits. The server picks the stored content type: the
 * client's value (a browser's guess, often empty) is only checked, never trusted.
 */
export function checkUploadRequest(input: Record<string, unknown>): Check<UploadRequest, UploadField> {
  const raw = typeof input.filename === "string" ? input.filename : "";
  // Only the last path segment counts: a browser never sends directories, and a crafted one is flattened.
  const filename = singleLine(raw.split(/[\\/]/).pop() ?? "");
  if (!filename || [...filename].length > 255 || hasControlCharacter(filename) || filename.startsWith(".")) {
    return { ok: false, field: "filename", message: "Use a file name of up to 255 characters." };
  }
  const extension = fileExtension(filename);
  if (!(UPLOAD_EXTENSIONS as string[]).includes(extension)) {
    return {
      ok: false,
      field: "filename",
      message: `That file type isn't supported. Use ${UPLOAD_EXTENSIONS.map((ext) => `.${ext}`).join(", ")}.`,
    };
  }
  const sizeBytes = input.sizeBytes;
  if (typeof sizeBytes !== "number" || !Number.isSafeInteger(sizeBytes) || sizeBytes <= 0) {
    return { ok: false, field: "sizeBytes", message: "The file is empty." };
  }
  if (sizeBytes > MAX_FILE_BYTES) return { ok: false, field: "sizeBytes", message: "Files can be up to 500 MB." };
  const allowed = UPLOAD_TYPES[extension as UploadExtension] as readonly string[];
  const declared = typeof input.contentType === "string" ? input.contentType.split(";")[0]!.trim().toLowerCase() : "";
  if (declared && declared !== "application/octet-stream" && !allowed.includes(declared)) {
    return { ok: false, field: "contentType", message: `That doesn't look like a .${extension} file.` };
  }
  return { ok: true, value: { filename, extension: extension as UploadExtension, sizeBytes, contentType: allowed[0]! } };
}

export interface UploadPartPlan {
  partNumber: number;
  sizeBytes: number;
}

/** Every part but the last is exactly `partBytes`; the last is the remainder. */
export function planUploadParts(sizeBytes: number, partBytes = UPLOAD_PART_BYTES): UploadPartPlan[] {
  const count = Math.max(1, Math.ceil(sizeBytes / partBytes));
  return Array.from({ length: count }, (_, index) => ({
    partNumber: index + 1,
    sizeBytes: index === count - 1 ? sizeBytes - partBytes * (count - 1) : partBytes,
  }));
}
